import { useRef, useState } from "react"
import Form, { useForm } from "../../gui/Form/Form"
import FormControl from "../../gui/Form/FormControl"
import Validators from "../../gui/Form/Validators"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import Button from "../../gui/Button/Button"
import TextInput, { Formatters, ITextInput } from "../../gui/TextInput/TextInput"
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker"
import { IDropdown } from "../../gui/Dropdown/Dropdown"
import usePortfolio from "../../hooks/PortfolioProvider"
import PortAccount from "../../models/PortAccount"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import PortUtils from "../../portlibs/PortUtils"
import GICHolding from "../../models/GICHolding"
import { TransactionType } from "../../shared/Consts"
import { usePath } from "../../libs/path/PathProvider"

export type TRedeemDlgProps = {
    holding:GICHolding
}
export default function RedeemDlg(props:TRedeemDlgProps){
    const {holding} = props
    const [busy,setBusy] = useState<boolean>(false)
    const refDiv = useRef<ITextInput|null>(null)
    const refQty = useRef<ITextInput|null>(null)
    const refAccount = useRef<IDropdown<number>|null>(null)
    const refDate = useRef<IDatePicker|null>(null)
    const {popOverlay} = useOverlay()
    const {valid} = useForm()
    const {portfolio} = usePortfolio()

    const qty = holding.gicTransactions.filter(trans => (trans.type==TransactionType.buy || trans.type==TransactionType.reinvest)).reduce((p,r) => p+r.quantity, 0)
    const dividend = qty*holding.yield/100
    const {addSnackbar} = useSnackbar()

    const onSave = async () => {
        if (busy) return
        else setBusy(false)

        const trans = holding.transactions[0].clone(true)
        trans.type = TransactionType.dividend
        trans.accountId = refAccount.current!.value!
        trans.price = 1
        trans.quantity = parseFloat(refDiv.current!.value!)
        trans.date = refDate.current!.value!
        trans.yield = 0
        trans.note = `${holding.id} interest`
        try { await PortAccount.i.update(portfolio, trans, false) }
        catch(e){ 
            addSnackbar(e)
            setBusy(false)
            return
        }

        const redeem = trans.clone(true)
        redeem.type = TransactionType.buy
        redeem.quantity = -parseFloat(refQty.current!.value!)
        redeem.note = `${holding.id} redeem`
        try { 
            await PortAccount.i.update(portfolio, redeem, true) 
            popOverlay(PortUtils.AddHoldingId)
        } catch(e){ addSnackbar(e) }
        finally { setBusy(false) }
    }

    return <>
        <h2 className="title">{holding.name}</h2>
        <div className="content">
            <div className="grid gap-16">
                <FormControl validators={[Validators.NonZeroNumber("Dividend")]}>
                    <TextInput ref={refDiv} placeholder="Dividend" float={true} evaluator={Formatters.Evaluate} defaultValue={dividend.toString()}/>
                </FormControl>
                <FormControl validators={[Validators.Required("Redeem")]}>
                    <TextInput ref={refQty} placeholder="Redeem" float={true} evaluator={Formatters.Evaluate} defaultValue={qty.toString()}/>
                </FormControl>
                <FormControl validators={[Validators.Required("Date")]}>
                    <DatePicker ref={refDate} placeholder="Date" float={true} defaultValue={new Date()}/>
                </FormControl>
            </div>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}

export function useRedeemDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const {hash,setHash} = usePath()
    const {portfolio} = usePortfolio()

    const afterClosed = () => {
        const {holdingId} = PortUtils.parseHash(hash)
        setHash(PortUtils.createHash(holdingId))
    }
    const addRedeem = (holdingId:string) => {
        const holding = portfolio.holding(holdingId) as GICHolding
        if (hasOverlay(PortUtils.AddHoldingId) || !holding) return
        addOverlay(PortUtils.AddHoldingId, {
            children:<Form><RedeemDlg holding={holding}/></Form>,
            className:"dialog",
            afterClosed
        })
    }
    return {addRedeem}
}